import React, { useState, useEffect, useCallback } from 'react';
import { Sparkles, RefreshCw, Loader2, AlertTriangle, Lightbulb, Search } from 'lucide-react';
import { adminAnalytics } from '../../services/api';
import InsightCard from './InsightCard';

/**
 * InsightsTab — Deterministic insight snapshots with manual refresh.
 *
 * Props:
 *   filters: { district_id, campus_id }
 *
 * Fix (2026-03-30): snapshot?.cards is the correct property (was: snapshot?.insights)
 */

const InsightsTab = ({ filters = {} }) => {
    const [snapshot, setSnapshot] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');

    const loadData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = {
                ...(filters.district_id && { district_id: filters.district_id }),
                ...(filters.campus_id && { campus_id: filters.campus_id }),
            };
            const res = await adminAnalytics.getInsightsLatest(params);
            setSnapshot(res.data?.snapshot || res.data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to load insights.');
        } finally {
            setLoading(false);
        }
    }, [filters.district_id, filters.campus_id]);

    useEffect(() => { loadData(); }, [loadData]);

    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            const params = {
                ...(filters.district_id && { district_id: filters.district_id }),
                ...(filters.campus_id && { campus_id: filters.campus_id }),
            };
            const res = await adminAnalytics.refreshInsights(params);
            setSnapshot(res.data?.snapshot || null);
        } catch {
            setError('Failed to refresh insights.');
        } finally {
            setRefreshing(false);
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 10 }}>
                <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#6366f1' }} />
                <span style={{ fontSize: 13, color: '#6b7280' }}>Loading insights…</span>
            </div>
        );
    }

    // FIX: backend returns `cards` not `insights`
    const cards = snapshot?.cards || [];

    // Extract investigation signal from period_comparison card if present
    const comparisonCard = cards.find(c => c.card_type === 'period_comparison');
    const investigateNext = comparisonCard?.data?.investigate_next;
    const investigateReason = comparisonCard?.data?.investigate_reason;

    return (
        <div>
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: 24,
            }}>
                <div>
                    <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Sparkles size={20} style={{ color: '#6366f1' }} />
                        Insights
                    </h2>
                    <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
                        {snapshot?.generated_at
                            ? `Generated ${new Date(snapshot.generated_at).toLocaleString()}`
                            : 'Deterministic insights from analytics data'
                        }
                    </p>
                </div>
                <button
                    onClick={handleRefresh}
                    disabled={refreshing}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '8px 16px', borderRadius: 8,
                        border: '1px solid #e2e8f0',
                        fontSize: 12, fontWeight: 500, cursor: refreshing ? 'default' : 'pointer',
                        background: '#fff', color: '#64748b',
                        opacity: refreshing ? 0.6 : 1,
                    }}
                >
                    <RefreshCw size={13} style={{
                        animation: refreshing ? 'spin 1s linear infinite' : 'none',
                    }} />
                    {refreshing ? 'Generating…' : 'Refresh Insights'}
                </button>
            </div>

            {error && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '10px 16px', background: '#fef2f2', borderRadius: 10,
                    marginBottom: 16, fontSize: 13, color: '#ef4444',
                }}>
                    <AlertTriangle size={14} /> {error}
                </div>
            )}

            {/* Decision prompt: what to investigate next */}
            {investigateNext && (
                <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    padding: '12px 16px', borderRadius: 10, marginBottom: 20,
                    background: '#fffbeb', border: '1px solid #fde68a',
                }}>
                    <Search size={16} style={{ color: '#d97706', marginTop: 1, flexShrink: 0 }} />
                    <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#92400e' }}>
                            Investigate Next: {investigateNext.replace(/_/g, ' ')}
                        </div>
                        {investigateReason && (
                            <div style={{ fontSize: 12, color: '#b45309', marginTop: 2 }}>
                                {investigateReason}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {cards.length === 0 ? (
                <div style={{
                    background: '#f8fafc', borderRadius: 14, padding: '48px 0',
                    textAlign: 'center', border: '1px solid #f1f5f9',
                }}>
                    <Lightbulb size={36} style={{ color: '#94a3b8', margin: '0 auto 12px', opacity: 0.5 }} />
                    <p style={{ fontSize: 15, fontWeight: 600, color: '#6b7280', margin: 0 }}>
                        No insights generated yet
                    </p>
                    <p style={{ fontSize: 13, color: '#94a3b8', marginTop: 6, maxWidth: 320, margin: '6px auto 0' }}>
                        Click "Refresh Insights" to generate analysis from current analytics data.
                    </p>
                </div>
            ) : (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: 14,
                }}>
                    {cards.map((card, i) => (
                        <InsightCard key={i} insight={card} />
                    ))}
                </div>
            )}

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default InsightsTab;
